import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * InterestTargetingInput — search-as-you-type autocomplete for Meta interest targeting.
 *
 * Props:
 *   interests  — array of { id, name } currently selected
 *   onChange   — callback with updated array
 *   disabled   — disables input
 *   companySlug — for scoped API URLs
 */
const InterestTargetingInput = ({ interests = [], onChange, disabled = false, companySlug }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const scopeUrl = (url) => {
    if (!companySlug) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}company_slug=${companySlug}`;
  };

  const formatAudienceSize = (lower, upper) => {
    if (!lower && !upper) return '';
    const format = (n) => {
      if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
      if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
      return n.toString();
    };
    if (lower && upper) return `${format(lower)} – ${format(upper)}`;
    if (lower) return `${format(lower)}+`;
    return `up to ${format(upper)}`;
  };

  const searchInterests = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const url = scopeUrl(`/api/facebook/targeting_search?q=${encodeURIComponent(searchQuery)}&type=adinterest`);
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      if (data.results) {
        // Filter out already-selected interests
        const selectedIds = new Set(interests.map(i => String(i.id)));
        setResults(data.results.filter(r => !selectedIds.has(String(r.id))));
      }
    } catch (e) {
      console.error('Interest search error:', e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [interests, companySlug]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowDropdown(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchInterests(val), 300);
  };

  const addInterest = (interest) => {
    const updated = [...interests, { id: interest.id, name: interest.name }];
    onChange(updated);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  const removeInterest = (id) => {
    onChange(interests.filter(i => String(i.id) !== String(id)));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {/* Selected interests as pills */}
      {interests.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
          {interests.map((interest) => (
            <span
              key={interest.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#EDE9FE',
                color: '#5B21B6',
                borderRadius: '9999px',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              {interest.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeInterest(interest.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#7C3AED',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '14px',
                    lineHeight: 1,
                    fontWeight: 700,
                  }}
                  aria-label={`Remove ${interest.name}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      {!disabled && (
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => query.length >= 2 && setShowDropdown(true)}
            placeholder="Search interests (e.g. comedy, fitness, cooking)..."
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #E5E5E5',
              borderRadius: '4px',
              fontSize: '0.875rem',
            }}
          />

          {/* Dropdown results */}
          {showDropdown && (query.length >= 2) && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              maxHeight: '200px',
              overflowY: 'auto',
              backgroundColor: '#fff',
              border: '1px solid #E5E5E5',
              borderTop: 'none',
              borderRadius: '0 0 4px 4px',
              zIndex: 10,
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            }}>
              {loading && (
                <div style={{ padding: '0.5rem 0.75rem', color: '#888', fontSize: '0.875rem' }}>Searching...</div>
              )}
              {!loading && results.length === 0 && (
                <div style={{ padding: '0.5rem 0.75rem', color: '#888', fontSize: '0.875rem' }}>No results found</div>
              )}
              {!loading && results.map((result) => (
                <button
                  key={result.id}
                  type="button"
                  onClick={() => addInterest(result)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.875rem',
                    borderBottom: '1px solid #F5F5F5',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F5F5'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span>{result.name}</span>
                  <span style={{ fontSize: '0.75rem', color: '#888' }}>
                    {formatAudienceSize(result.audience_size_lower_bound, result.audience_size_upper_bound)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InterestTargetingInput;
